import axios from 'axios';
import { clearTokens, getAccessToken, getAuthSource, getIdToken, getRefreshToken, getTenantName, setAccessToken } from '../auth/tokenStorage';
import { decodeJwtPayload } from '../auth/decodeJwt';

export const client = axios.create({
  baseURL: '/api',
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Blueprint Runtime client (port 8017) ──────────────
export const bpClient = axios.create({
  baseURL: '/bpapi',
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the IAM access token to every request.
function attachAuth(config: any) {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}

// The access token is short-lived (IAM_ACCESS_TOKEN_TTL_SECONDS, default 1hr); the refresh
// token lives much longer (IAM_REFRESH_TOKEN_TTL_SECONDS, default 30d). Without this, every
// user gets bounced to the login page on the hour even though a perfectly good refresh token
// is sitting in localStorage. Plain axios (not bpClient) so this call never recurses through
// its own 401 handler. Single in-flight promise so concurrent 401s share one refresh call.
let refreshPromise: Promise<string | null> | null = null;
function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  // 2026-08-21: a CIAM/tenant session's refresh_token is a real Cognito token -- the local-admin
  // /refresh route below can't validate one (found live: a tenant login briefly showed the
  // dashboard then bounced straight back to login, because this call 401'd unconditionally for
  // every session type and the interceptor treated that as "refresh failed, log out"). Tenant
  // sessions need tenant_name + username (Cognito's SECRET_HASH requires the original username)
  // -- tenant_name is already in storage; username comes from the ID token's own claims, same
  // precedence AuthContext.tsx's userFromIdToken() already uses.
  const authSource = getAuthSource();
  const request = authSource === 'ciam'
    ? (() => {
        const tenantName = getTenantName();
        const idToken = getIdToken();
        const claims = idToken ? decodeJwtPayload(idToken) : null;
        // Deliberately NOT the same email-first precedence userFromIdToken() uses for display --
        // Cognito's REFRESH_TOKEN_AUTH flow (plain InitiateAuth, used for a client-secret pool)
        // verifies SECRET_HASH against the pool's own internal username (the sub, in an
        // email-alias pool), not the alias a person actually logs in with, even though the
        // ADMIN_NO_SRP_AUTH login flow accepts either. Found live: refresh 401'd with Cognito's
        // own "Unable to verify secret hash for client ..." using the email.
        const username = claims?.['cognito:username'] || claims?.sub;
        if (!tenantName || !username) return Promise.reject(new Error('Missing tenant session identity for refresh.'));
        return axios.post('/bpapi/blueprint-runtime/iam/auth/refresh-tenant', {
          tenant_name: tenantName, username, refresh_token: refreshToken,
        });
      })()
    : axios.post('/bpapi/blueprint-runtime/iam/auth/refresh', { refresh_token: refreshToken });

  refreshPromise = request
    .then((res) => {
      setAccessToken(res.data.AccessToken);
      return res.data.AccessToken as string;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function redirectToLogin() {
  clearTokens();
  if (!window.location.pathname.includes('/login')) window.location.assign('/ui/login');
}

async function handle401(error: any) {
  const original = error.config;
  if (error.response?.status !== 401 || !original || original._retriedAfterRefresh) {
    if (error.response?.status === 401) redirectToLogin();
    return Promise.reject(error);
  }
  original._retriedAfterRefresh = true;
  const newToken = await refreshAccessToken();
  if (!newToken) {
    redirectToLogin();
    return Promise.reject(error);
  }
  original.headers.Authorization = `Bearer ${newToken}`;
  return axios(original);
}
client.interceptors.request.use(attachAuth);
bpClient.interceptors.request.use(attachAuth);
client.interceptors.response.use((r) => r, handle401);
bpClient.interceptors.response.use((r) => r, handle401);

// ── System ─────────────────────────────────────────────
export const getHealth = () => client.get('/health');
export const getRuntimeStatus = (environment?: string) =>
  client.get('/runtime/status', { params: environment ? { environment } : {} });

// ── Connections ────────────────────────────────────────
export const getConnections = (params?: Record<string, unknown>) =>
  client.get('/connections', { params });
export const createConnection = (data: Record<string, unknown>) =>
  client.post('/connections', data);
export const createConnectionWizard = (data: Record<string, unknown>) =>
  client.post('/connections/wizard', data);
export const updateConnection = (id: string, data: Record<string, unknown>) =>
  client.put(`/connections/${encodeURIComponent(id)}`, data);
export const deleteConnection = (id: string) =>
  client.delete(`/connections/${encodeURIComponent(id)}`);
export const probeConnection = (id: string) =>
  client.post(`/connections/${encodeURIComponent(id)}/probe`);
export const promoteConnection = (id: string, data: Record<string, unknown>) =>
  client.post(`/connections/${encodeURIComponent(id)}/promote`, data);

// ── Environments ───────────────────────────────────────
export const getEnvironments = (params?: Record<string, unknown>) =>
  client.get('/environments', { params });
export const createEnvironment = (data: Record<string, unknown>) =>
  client.post('/environments', data);
export const updateEnvironment = (name: string, data: Record<string, unknown>) =>
  client.put(`/environments/${encodeURIComponent(name)}`, data);
export const deleteEnvironment = (name: string, cascade = false) =>
  client.delete(`/environments/${encodeURIComponent(name)}`, { params: cascade ? { cascade: true } : {} });

// ── Discovery ──────────────────────────────────────────
export const runDiscovery = (connectionIds: string[], setBaseline = false) =>
  client.post('/discovery/run', {
    connection_ids: connectionIds,
    set_as_baseline: setBaseline,
  });
export const getSnapshots = (connectionId: string) =>
  client.get(`/discovery/connections/${encodeURIComponent(connectionId)}/snapshots`);
export const checkDrift = (connectionId: string, discoveryRunId?: string) =>
  client.get(`/discovery/connections/${encodeURIComponent(connectionId)}/drift`, {
    params: discoveryRunId ? { discovery_run_id: discoveryRunId } : undefined,
  });
export const acceptDrift = (connectionId: string) =>
  client.post(`/discovery/connections/${encodeURIComponent(connectionId)}/drift/accept`);
// Explicitly set an arbitrary (past or current) snapshot as the connection's baseline --
// unlike acceptDrift (always promotes whatever snapshot is newest), this targets a specific
// snapshot_id from getSnapshots(), and can be called again later with a different one.
export const setBaseline = (connectionId: string, snapshotId: string) =>
  client.post(`/discovery/connections/${encodeURIComponent(connectionId)}/baseline`, { snapshot_id: snapshotId });
export const downloadConnectionSchema = (connectionId: string) =>
  client.get(`/discovery/connections/${encodeURIComponent(connectionId)}/schema/download`, {
    responseType: 'blob',
  });
export const downloadAllSchemas = () =>
  client.get('/discovery/schemas/download', { responseType: 'blob' });

// ── Catalog ────────────────────────────────────────────
export const getCatalogAssets = (params?: Record<string, unknown>) =>
  client.get('/catalog/assets', { params });
export const getCatalogAsset = (assetId: string) =>
  client.get(`/catalog/assets/${encodeURIComponent(assetId)}`);
export const validateCatalogAsset = (assetId: string) =>
  client.post(`/catalog/assets/${encodeURIComponent(assetId)}/validate`);
export const getCatalogReadiness = (playbookId?: string) =>
  client.get('/catalog/readiness', {
    params: playbookId ? { playbook_id: playbookId } : {},
  });
export const triggerCatalogImport = (data: Record<string, unknown>) =>
  client.post('/catalog/import', data);
export const getImportJob = (jobId: string) =>
  client.get(`/catalog/import/${encodeURIComponent(jobId)}`);
export const getCatalogArtifact = (playbookId?: string) =>
  client.get('/catalog/artifact', {
    params: playbookId ? { playbook_id: playbookId } : {},
  });
export const downloadArtifact = (playbookId?: string) =>
  client.get('/catalog/artifact/download', {
    params: playbookId ? { playbook_id: playbookId } : {},
    responseType: 'blob',
  });
export const getDriftHistory = (connectionId?: string, environment?: string, discoveryRunId?: string) =>
  client.get('/catalog/drift/history', {
    params: {
      ...(connectionId ? { connection_id: connectionId } : {}),
      ...(environment ? { environment } : {}),
      ...(discoveryRunId ? { discovery_run_id: discoveryRunId } : {}),
    },
  });

// ── Lifecycle workspace ────────────────────────────────
export const getConnectionOverview = (id: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/overview`);

export const getDiscoveryScope = (id: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/discovery-scope`);
export const saveDiscoveryScope = (id: string, data: Record<string, unknown>) =>
  client.post(`/connections/${encodeURIComponent(id)}/discovery-scope`, data);

export const startDiscoveryRun = (id: string) =>
  client.post(`/connections/${encodeURIComponent(id)}/discovery-runs`);
export const listDiscoveryRuns = (id: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/discovery-runs`);
export const getDiscoveryRun = (id: string, runId: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/discovery-runs/${encodeURIComponent(runId)}`);
export const getRunSnapshot = (id: string, runId: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/discovery-runs/${encodeURIComponent(runId)}/snapshot`);
export const getRunCatalogDraft = (id: string, runId: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/discovery-runs/${encodeURIComponent(runId)}/catalog-draft`);

export const listCatalogs = (id: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/catalogs`);
export const getCatalog = (id: string, catalogId: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/catalogs/${encodeURIComponent(catalogId)}`);
export const listCatalogAssets = (id: string, catalogId: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/catalogs/${encodeURIComponent(catalogId)}/assets`);
export const getCatalogAssetDetail = (id: string, catalogId: string, assetId: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/catalogs/${encodeURIComponent(catalogId)}/assets/${encodeURIComponent(assetId)}`);
export const getSensitivityReport = (id: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/sensitivity-report`);
// Human approve/reject on one flagged field -- entirely local to RS, no Control Plane / LLM
// call involved (2026-08-26 direct instruction: RS cannot depend on reaching CP for this).
export const reviewSensitivityField = (connectionId: string, fieldId: string, status: 'approved' | 'rejected') =>
  client.post(
    `/connections/${encodeURIComponent(connectionId)}/sensitivity-report/fields/${encodeURIComponent(fieldId)}/review`,
    { status },
  );
export const listAssetFields = (id: string, catalogId: string, assetId: string) =>
  client.get(`/connections/${encodeURIComponent(id)}/catalogs/${encodeURIComponent(catalogId)}/assets/${encodeURIComponent(assetId)}/fields`);

// ── Platform ───────────────────────────────────────────
export const getPlatformJobs = (params?: Record<string, unknown>) =>
  client.get('/platform/jobs', { params });
export const deployPlatform = (data: Record<string, unknown>) =>
  client.post('/platform/deploy', data);
export const triggerPlatform = (jobId: string, params?: Record<string, unknown>) =>
  client.post('/platform/trigger', { job_id: jobId, params: params ?? {} });
export const getPlatformRun = (jobId: string) =>
  client.get(`/platform/runs/${encodeURIComponent(jobId)}`);
export const collectResults = (jobId: string) =>
  client.post(`/platform/collect/${encodeURIComponent(jobId)}`);
export const teardownPlatform = (jobId: string) =>
  client.post(`/platform/teardown/${encodeURIComponent(jobId)}`);

// ── Blueprint Runtime API helpers ──────────────────────
// RAM-171: todayStart (ISO instant, browser-local midnight) + environment fix the Platform
// Dashboard's Usage card not reconciling with the Usage page -- same browser-local-time /
// explicit-environment-scoping convention already used by getUsageSummary/getUsageDaily.
export const getBpDashboard = (opts?: { todayStart?: string; environment?: string }) =>
  bpClient.get('/blueprint-runtime/dashboard', {
    params: {
      ...(opts?.todayStart ? { today_start: opts.todayStart } : {}),
      ...(opts?.environment ? { environment: opts.environment } : {}),
    },
  });
export const getBpPackages = (params?: Record<string, unknown>) =>
  bpClient.get('/blueprint-runtime/packages', { params });
export const getBpPackage = (packageId: string) =>
  bpClient.get(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}`);
export const getBpPackageBindings = (packageId: string) =>
  bpClient.get(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/bindings`);
export const createConnectionBinding = (packageId: string, data: Record<string, unknown>) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/bindings/connections`, data);
export const createCatalogBinding = (packageId: string, data: Record<string, unknown>) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/bindings/catalog`, data);
export const runCompatibilityCheck = (packageId: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/compatibility-check`);
export const installPackage = (packageId: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/install`);
export const activatePackage = (packageId: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/activate`);
export const publishPackage = (packageId: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/publish`);
export const unpublishPackage = (packageId: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/unpublish`);
export const attachPackage = (packageId: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/attach`);
export const detachPackage = (packageId: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/detach`);
export const healthCheckPackage = (packageId: string) =>
  bpClient.get(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/health-check`);
export const getPackageArtifacts = (packageId: string) =>
  bpClient.get(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/artifacts`);
export const publishTarget = (packageId: string, kind: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/targets/${encodeURIComponent(kind)}/publish`);
export const unpublishTarget = (packageId: string, kind: string) =>
  bpClient.post(`/blueprint-runtime/packages/${encodeURIComponent(packageId)}/targets/${encodeURIComponent(kind)}/unpublish`);

// ── Usage & Billing (Part B) ────────────────────────────
// rangeStart/rangeEnd are full ISO-8601 timestamps (WITH offset, e.g. from
// `new Date(...).toISOString()`) -- used for the Usage page's custom date/time
// range filter, always in the BROWSER's own local time converted to an exact
// UTC instant, never re-interpreted as a UTC calendar day server-side.
export const getUsageSummary = (environment?: string, opts?: { date?: string; rangeStart?: string; rangeEnd?: string; packageId?: string }) =>
  bpClient.get('/blueprint-runtime/usage/summary', {
    params: {
      ...(environment ? { environment } : {}),
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.rangeStart ? { range_start: opts.rangeStart } : {}),
      ...(opts?.rangeEnd ? { range_end: opts.rangeEnd } : {}),
      ...(opts?.packageId ? { package_id: opts.packageId } : {}),
    },
  });
export const exportUsage = (period?: string, environment?: string) =>
  bpClient.post('/blueprint-runtime/usage/export', null, {
    params: { ...(period ? { period } : {}), ...(environment ? { environment } : {}) },
    responseType: 'blob',
  });
// Per-calendar-day breakdown (call counts + bytes, aggregated across all packages) for the
// Usage page's "Daily breakdown" view + matching CSV export -- same date/range params as
// getUsageSummary, see that function's own comment for the local-time/UTC-instant convention.
export const getUsageDaily = (environment?: string, opts?: { date?: string; rangeStart?: string; rangeEnd?: string; packageId?: string }) =>
  bpClient.get('/blueprint-runtime/usage/daily', {
    params: {
      ...(environment ? { environment } : {}),
      ...(opts?.date ? { date: opts.date } : {}),
      ...(opts?.rangeStart ? { range_start: opts.rangeStart } : {}),
      ...(opts?.rangeEnd ? { range_end: opts.rangeEnd } : {}),
      ...(opts?.packageId ? { package_id: opts.packageId } : {}),
    },
  });

// ── Runtime MCP test passthrough (proxied via l2-api -> mcp-server) ────
// Named Runtime* to disambiguate from the Fabric-side MCP *connection* functions above
// (getMcpTools/getMcpResources there operate on a connection id via `client`, not a
// package/playbook name via `bpClient` -- different domain entirely).
export const getRuntimeMcpTools = (packageName: string) =>
  bpClient.get('/runtime-mcp/tools', { params: { playbook_name: packageName } });
export const getRuntimeMcpResources = (packageName: string) =>
  bpClient.get('/runtime-mcp/resources', { params: { playbook_name: packageName } });
export const readRuntimeMcpResource = (uri: string, packageName: string) =>
  bpClient.get('/runtime-mcp/resources/read', { params: { uri, playbook_name: packageName } });
export const callRuntimeMcpTool = (
  name: string,
  args: Record<string, unknown>,
  packageName: string,
  opts?: { timeoutMs?: number },
) =>
  bpClient.post(
    '/runtime-mcp/tools/call',
    { name, arguments: args, playbook_name: packageName },
    opts?.timeoutMs ? { timeout: opts.timeoutMs } : undefined,
  );
export const importFromBrowser = (formData: FormData) =>
  bpClient.post('/blueprint-runtime/packages/import/browser', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const getImportJobStatus = (jobId: string) =>
  bpClient.get(`/blueprint-runtime/package-import-jobs/${encodeURIComponent(jobId)}`);
export const getBpRuntimeManagerStatus = () =>
  bpClient.get('/blueprint-runtime/runtime-manager/status');
export const getLlmStatus = () =>
  bpClient.get('/blueprint-runtime/runtime-manager/llm-status');
export type LlmProvider = 'openai' | 'anthropic' | 'gemini' | 'custom';
export interface LlmSettingsPayload {
  provider: LlmProvider;
  model: string;
  api_key?: string;
  base_url?: string;
}
export const getLlmSettings = () =>
  bpClient.get('/blueprint-runtime/llm-settings');
export const saveLlmSettings = (data: LlmSettingsPayload) =>
  bpClient.put('/blueprint-runtime/llm-settings', data);
export const testLlmSettings = (data: LlmSettingsPayload) =>
  bpClient.post('/blueprint-runtime/llm-settings/test', data, { timeout: 45_000 });
export const getFabricConnections = () =>
  client.get('/fabric/connections');
export const getBpHealth = () =>
  bpClient.get('/health');
export const getFabricHealth = () =>
  client.get('/health');
export const restartFabric = () =>
  bpClient.post('/blueprint-runtime/admin/restart-fabric');
export const restartRuntime = () =>
  bpClient.post('/blueprint-runtime/admin/restart-runtime');

// ── API / Application Connections ──────────────────────
export const createApiConnectionWizard = (data: Record<string, unknown>) =>
  client.post('/api-connections/wizard', data);
export const getApiConnections = (params?: Record<string, unknown>) =>
  client.get('/api-connections', { params });
export const getApiConnection = (id: string) =>
  client.get(`/api-connections/${encodeURIComponent(id)}`);
export const updateApiConnection = (id: string, data: Record<string, unknown>) =>
  client.put(`/api-connections/${encodeURIComponent(id)}`, data);
export const deleteApiConnection = (id: string) =>
  client.delete(`/api-connections/${encodeURIComponent(id)}`);
export const validateApiConnection = (id: string) =>
  client.post(`/api-connections/${encodeURIComponent(id)}/validate`);
export const getApiConnectionResources = (id: string) =>
  client.get(`/api-connections/${encodeURIComponent(id)}/resources`);
export const importApiContract = (id: string, data: Record<string, unknown>) =>
  client.post(`/api-connections/${encodeURIComponent(id)}/contract/import`, data);
export const testApiConnectionLive = (data: Record<string, unknown>) =>
  client.post('/api-connections/test', data);
export const getApiConnectionAudit = (id: string) =>
  client.get(`/api-connections/${encodeURIComponent(id)}/audit`);
export const refreshApiConnectionSession = (id: string) =>
  client.post(`/api-connections/${encodeURIComponent(id)}/session/refresh`);

// ── Data Platform Connections ──────────────────────────
export const createPlatformConnectionWizard = (data: Record<string, unknown>) =>
  client.post('/platform-connections/wizard', data);
export const testPlatformConnectionLive = (data: Record<string, unknown>) =>
  client.post('/platform-connections/test', data);
export const getPlatformConnections = (params?: Record<string, unknown>) =>
  client.get('/platform-connections', { params });
export const getPlatformConnection = (id: string) =>
  client.get(`/platform-connections/${encodeURIComponent(id)}`);
export const updatePlatformConnection = (id: string, data: Record<string, unknown>) =>
  client.put(`/platform-connections/${encodeURIComponent(id)}`, data);
export const deletePlatformConnection = (id: string) =>
  client.delete(`/platform-connections/${encodeURIComponent(id)}`);
export const validatePlatformConnection = (id: string) =>
  client.post(`/platform-connections/${encodeURIComponent(id)}/validate`);
export const getPlatformConnectionAudit = (id: string) =>
  client.get(`/platform-connections/${encodeURIComponent(id)}/audit`);

// ── File / Object Store Connections ───────────────────
export const createFileConnectionWizard = (data: Record<string, unknown>) =>
  client.post('/file-connections/wizard', data);
export const testFileConnectionLive = (data: Record<string, unknown>) =>
  client.post('/file-connections/test', data);
export const getFileConnections = (params?: Record<string, unknown>) =>
  client.get('/file-connections', { params });
export const getFileConnection = (id: string) =>
  client.get(`/file-connections/${encodeURIComponent(id)}`);
export const updateFileConnection = (id: string, data: Record<string, unknown>) =>
  client.put(`/file-connections/${encodeURIComponent(id)}`, data);
export const deleteFileConnection = (id: string) =>
  client.delete(`/file-connections/${encodeURIComponent(id)}`);
export const validateFileConnection = (id: string) =>
  client.post(`/file-connections/${encodeURIComponent(id)}/validate`);
export const getFileConnectionAudit = (id: string) =>
  client.get(`/file-connections/${encodeURIComponent(id)}/audit`);

// ── Streaming / Event Stream Connections ──────────────
export const createStreamingConnectionWizard = (data: Record<string, unknown>) =>
  client.post('/streaming-connections/wizard', data);
export const testStreamingConnectionLive = (data: Record<string, unknown>) =>
  client.post('/streaming-connections/test', data);
export const getStreamingConnections = (params?: Record<string, unknown>) =>
  client.get('/streaming-connections', { params });
export const getStreamingConnection = (id: string) =>
  client.get(`/streaming-connections/${encodeURIComponent(id)}`);
export const updateStreamingConnection = (id: string, data: Record<string, unknown>) =>
  client.put(`/streaming-connections/${encodeURIComponent(id)}`, data);
export const deleteStreamingConnection = (id: string) =>
  client.delete(`/streaming-connections/${encodeURIComponent(id)}`);
export const validateStreamingConnection = (id: string) =>
  client.post(`/streaming-connections/${encodeURIComponent(id)}/validate`);
export const getStreamingConnectionAudit = (id: string) =>
  client.get(`/streaming-connections/${encodeURIComponent(id)}/audit`);

// ── Audit Trail ────────────────────────────────────────
export const getAuditEvents = (params?: Record<string, unknown>) =>
  client.get('/audit/events', { params });

// ── Catalog Connections ────────────────────────────────
export const getCatalogConnections = (params?: Record<string, unknown>) =>
  client.get('/catalog-connections', { params });
export const createCatalogConnectionWizard = (data: Record<string, unknown>) =>
  client.post('/catalog-connections/wizard', data);
export const updateCatalogConnection = (id: string, data: Record<string, unknown>) =>
  client.put(`/catalog-connections/${encodeURIComponent(id)}`, data);
export const deleteCatalogConnection = (id: string) =>
  client.delete(`/catalog-connections/${encodeURIComponent(id)}`);
export const validateCatalogConnection = (id: string) =>
  client.post(`/catalog-connections/${encodeURIComponent(id)}/validate`);

// ── License Management (RAM-187 Phase 1) ───────────────
// All 6 routes live on Fabric itself (license_routes.py, mounted at /license) -- reached via
// the plain `client` (baseURL /api -> :8015), same as every other Fabric-native call in this
// file, not `bpClient`.
export const getLicenseStatus = () => client.get('/license/status');
export const getLicenseEntitlements = () => client.get('/license/entitlements');
// Direct user request, 2026-08-09: the FULL installed license record -- provenance
// (installed_at/by, signing key, tamper-check hash) + the complete original signed claims
// verbatim, distinct from /status's live-computed summary. Backs LicenseManagement.tsx's
// expandable "Installed License Details" section.
export const getInstalledLicenseDetails = () => client.get('/license/installed');
export const getLicenseHistory = (limit?: number) =>
  client.get('/license/history', { params: limit ? { limit } : {} });
export const createLicenseActivationRequest = (requestedTenantId?: string) =>
  client.post('/license/activation-request', { requested_tenant_id: requestedTenantId || undefined });
export const installLicense = (licenseJws: string, installedBy?: string, trustedSigningKeys?: Record<string, unknown>) =>
  client.post('/license/install', {
    license_jws: licenseJws,
    installed_by: installedBy || undefined,
    // RAM-208: optional JWKS-shape bundle riding along in a downloaded artifact -- see
    // LicenseManagement.tsx's InstallLicenseForm.onFile for where this gets unwrapped.
    trusted_signing_keys: trustedSigningKeys || undefined,
  });
// RAM-190: {"installed": false} (no real license yet) or the full LicenseContext shape --
// TenantIdField.tsx's own useQuery consumes this to resolve the license-bound tenant.
export const getLicenseContext = () => client.get('/license/context');

// ── MCP Server Connections ─────────────────────────────
export const getMcpConnections = (params?: Record<string, unknown>) =>
  client.get('/mcp-connections', { params });
export const getMcpConnection = (id: string) =>
  client.get(`/mcp-connections/${encodeURIComponent(id)}`);
export const createMcpConnectionWizard = (data: Record<string, unknown>) =>
  client.post('/mcp-connections/wizard', data);
export const updateMcpConnection = (id: string, data: Record<string, unknown>) =>
  client.put(`/mcp-connections/${encodeURIComponent(id)}`, data);
export const deleteMcpConnection = (id: string) =>
  client.delete(`/mcp-connections/${encodeURIComponent(id)}`);
export const validateMcpConnection = (id: string) =>
  client.post(`/mcp-connections/${encodeURIComponent(id)}/validate`);
export const discoverMcpCapabilities = (id: string) =>
  client.post(`/mcp-connections/${encodeURIComponent(id)}/discover`);
export const getMcpTools = (id: string) =>
  client.get(`/mcp-connections/${encodeURIComponent(id)}/tools`);
export const getMcpResources = (id: string) =>
  client.get(`/mcp-connections/${encodeURIComponent(id)}/resources`);
export const getMcpPrompts = (id: string) =>
  client.get(`/mcp-connections/${encodeURIComponent(id)}/prompts`);
export const getMcpConnectionAudit = (id: string) =>
  client.get(`/mcp-connections/${encodeURIComponent(id)}/audit`);

// ── Connector Simulation Lab ───────────────────────────
// Safe dry-runs only: these endpoints never persist a connection, accept a
// credential value, or perform an outbound network call.
export const getConnectorSimulationPresets = () =>
  client.get('/connector-simulations/presets');
export const runConnectorSimulation = (data: Record<string, unknown>) =>
  client.post('/connector-simulations/run', data);
